/**
 * Transfer request details and manual provider assignment for the service request drawer.
 */

import {
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { useMedplum } from '@medplum/react-hooks';
import type { QuestionnaireResponse, ServiceRequest } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import {
  executeTransferFulfillment,
  fetchOtherEpisodeCareManagerIds,
  filterPractitionersForTransferFulfillment,
} from '../../../lib/transferFulfillmentService';
import {
  extractServiceRequestPreferences,
  extractTransferQrItems,
  getTransferQRForEpisode,
} from '../../../lib/transferService';
import {
  TRANSFER_REASON_DISPLAY,
  isTransferReasonCode,
} from '../../../lib/transferFhirConstants';
import type { EpisodeRow } from '../types/lifecycle';

function reasonLabel(code: string): string {
  return isTransferReasonCode(code) ? TRANSFER_REASON_DISPLAY[code] : code;
}

export interface TransferRequestTabProps {
  episode: EpisodeRow;
  serviceRequest?: ServiceRequest;
  /** When true, show row-level mock fields instead of fetching Medplum QR. */
  useMockData?: boolean;
  onTransferComplete?: () => void;
}

export function TransferRequestTab({
  episode,
  serviceRequest,
  useMockData = false,
  onTransferComplete,
}: TransferRequestTabProps): JSX.Element {
  const medplum = useMedplum();
  const [qr, setQr] = useState<QuestionnaireResponse | undefined>();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [practitionerOptions, setPractitionerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [outgoingProviderId, setOutgoingProviderId] = useState<string | undefined>();
  const [receivingProviderId, setReceivingProviderId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (useMockData) {
      setQr(undefined);
      return;
    }

    const episodeId = episode.episodeOfCareId;
    if (!episodeId) return;

    let cancelled = false;
    setLoadingDetails(true);
    void getTransferQRForEpisode(medplum, episodeId, {
      patientId: episode.clientId,
    })
      .then((result) => {
        if (!cancelled) setQr(result);
      })
      .catch(() => {
        if (!cancelled) setQr(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [useMockData, episode.episodeOfCareId, episode.clientId, medplum]);

  const fetchPractitioners = useCallback(async () => {
    if (useMockData) {
      setPractitionerOptions([]);
      return;
    }
    setLoadingPractitioners(true);
    try {
      let careManagerId: string | undefined;
      if (episode.episodeOfCareId) {
        try {
          const eoc = await medplum.readResource(
            'EpisodeOfCare',
            episode.episodeOfCareId,
          );
          careManagerId = eoc.careManager?.reference?.replace(/^Practitioner\//, '');
        } catch {
          careManagerId = undefined;
        }
      }
      setOutgoingProviderId(careManagerId);

      const excludePractitionerIds = new Set<string>();
      if (careManagerId) {
        excludePractitionerIds.add(careManagerId);
      }
      if (episode.episodeOfCareId && episode.clientId) {
        for (const id of await fetchOtherEpisodeCareManagerIds(
          medplum,
          episode.clientId,
          episode.episodeOfCareId,
        )) {
          excludePractitionerIds.add(id);
        }
      }

      const practitioners = await medplum.searchResources('Practitioner', {
        active: 'true',
        _count: '100',
      });
      const options = filterPractitionersForTransferFulfillment(practitioners, {
        excludePractitionerIds: [...excludePractitionerIds],
      })
        .map((p) => ({
          value: p.id,
          label: formatPractitionerStaffLabel(p) || p.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setPractitionerOptions(options);
    } catch {
      setOutgoingProviderId(undefined);
      setPractitionerOptions([]);
    } finally {
      setLoadingPractitioners(false);
    }
  }, [medplum, useMockData, episode.episodeOfCareId, episode.clientId]);

  useEffect(() => {
    if (outgoingProviderId && receivingProviderId === outgoingProviderId) {
      setReceivingProviderId(null);
    }
  }, [outgoingProviderId, receivingProviderId]);

  useEffect(() => {
    void fetchPractitioners();
  }, [fetchPractitioners]);

  const handleAssignProvider = useCallback(async () => {
    if (!serviceRequest?.id || !receivingProviderId) return;
    setSubmitting(true);
    try {
      await executeTransferFulfillment(medplum, {
        serviceRequestId: serviceRequest.id,
        receivingPractitionerId: receivingProviderId,
      });
      medplum.invalidateSearches('ServiceRequest');
      medplum.invalidateSearches('EpisodeOfCare');
      medplum.invalidateSearches('CareTeam');
      notifications.show({
        title: 'Transfer complete',
        message: `${episode.clientName ?? 'Client'} assigned to a new provider.`,
        color: 'green',
      });
      onTransferComplete?.();
    } catch (err) {
      notifications.show({
        title: 'Transfer failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    serviceRequest?.id,
    receivingProviderId,
    medplum,
    episode.clientName,
    onTransferComplete,
  ]);

  const qrItems = qr ? extractTransferQrItems(qr) : undefined;
  const prefs = serviceRequest
    ? extractServiceRequestPreferences(serviceRequest)
    : undefined;

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            From provider
          </Text>
          <Text size="sm">{episode.fromProvider ?? '—'}</Text>
        </div>
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Requested
          </Text>
          <Text size="sm">{episode.transferRequestedAt ?? '—'}</Text>
        </div>
        {episode.transferStatus && (
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Status
            </Text>
            <Badge variant="light" color="yellow" size="sm">
              {episode.transferStatus}
            </Badge>
          </div>
        )}
      </SimpleGrid>

      <Divider />

      {loadingDetails ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <>
          {(qrItems?.transferReasons.length ?? 0) > 0 && (
            <>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Transfer reasons
              </Text>
              <Group gap="xs" wrap="wrap">
                {qrItems!.transferReasons.map((code) => (
                  <Badge key={code} variant="light" color="gray">
                    {reasonLabel(code)}
                  </Badge>
                ))}
              </Group>
            </>
          )}

          {qrItems?.transferExplanation && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Transfer explanation
              </Text>
              <Textarea
                value={qrItems.transferExplanation}
                readOnly
                minRows={3}
                styles={{
                  input: { backgroundColor: 'var(--mantine-color-gray-0)' },
                }}
              />
            </div>
          )}

          {qrItems?.recommendedClinicians && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Recommended clinicians
              </Text>
              <Text size="sm">{qrItems.recommendedClinicians}</Text>
            </div>
          )}

          {!qrItems && episode.transferReason && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Reason
              </Text>
              <Text size="sm">{episode.transferReason}</Text>
            </div>
          )}

          {prefs &&
            (prefs.sessionFormat ||
              (prefs.areasOfFocus?.length ?? 0) > 0 ||
              (prefs.providerPreferences?.length ?? 0) > 0 ||
              (prefs.availability?.length ?? 0) > 0) && (
              <>
                <Divider label="Matching preferences" labelPosition="left" />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  {prefs.sessionFormat && (
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Session format
                      </Text>
                      <Text size="sm">{prefs.sessionFormat}</Text>
                    </div>
                  )}
                  {prefs.areasOfFocus && prefs.areasOfFocus.length > 0 && (
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Areas of focus
                      </Text>
                      <Text size="sm">{prefs.areasOfFocus.join(', ')}</Text>
                    </div>
                  )}
                  {prefs.providerPreferences &&
                    prefs.providerPreferences.length > 0 && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Provider preferences
                        </Text>
                        <Text size="sm">
                          {prefs.providerPreferences.join(', ')}
                        </Text>
                      </div>
                    )}
                  {prefs.availability && prefs.availability.length > 0 && (
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Availability
                      </Text>
                      <Text size="sm">{prefs.availability.join(', ')}</Text>
                    </div>
                  )}
                </SimpleGrid>
              </>
            )}

          {!loadingDetails &&
            !qrItems &&
            !episode.transferReason &&
            !prefs?.sessionFormat &&
            !(prefs?.areasOfFocus?.length ?? 0) &&
            !(prefs?.providerPreferences?.length ?? 0) &&
            !(prefs?.availability?.length ?? 0) && (
              <Text size="sm" c="dimmed">
                No transfer details recorded for this request.
              </Text>
            )}
        </>
      )}

      <Divider label="Assign new provider" labelPosition="left" mt="md" />

      {useMockData ? (
        <Text size="sm" c="dimmed">
          Manual provider assignment is available in live mode only.
        </Text>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Until automated matching is enabled, assign the receiving provider here.
            This updates the episode care manager, care team membership, compartment
            access, and moves the service request to Fulfilled.
          </Text>
          <Select
            label="New provider"
            placeholder={
              loadingPractitioners ? 'Loading providers…' : 'Select provider'
            }
            data={practitionerOptions}
            value={receivingProviderId}
            onChange={setReceivingProviderId}
            searchable
            nothingFoundMessage="No providers found"
            disabled={loadingPractitioners || submitting}
          />
          <Group justify="flex-end">
            <Button
              onClick={() => void handleAssignProvider()}
              loading={submitting}
              disabled={!receivingProviderId || !serviceRequest?.id}
            >
              Complete transfer
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
